// frontend/src/pages/UpdatePasswordPage.tsx
// PRC-017 · A6: crear una contraseña nueva desde el enlace del email.
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { supabase } from '../config/supabaseClient';
import { SimpleLayout } from '../modules/pricingJourney/components/JourneyLayouts';
import { Notice, StateIcon, StatusBlock } from '../modules/pricingJourney/components/JourneyBlocks';
import { Eyebrow, JourneyButton, JourneyCard, JourneyText } from '../modules/pricingJourney/components/JourneyBasics';
import { J } from '../modules/pricingJourney/journeyTokens';
import PasswordField from '../modules/pricingJourney/components/PasswordField';
import { focusFirstError, linkErrorInUrl, MIN_PASSWORD_LENGTH, OFFLINE_MESSAGE } from '../modules/pricingJourney/journeyAuth';

// Tiempo máximo para que Supabase lea la sesión del enlace; nunca «enlace inválido» antes.
const SESSION_WAIT_MS = 6000;

const UpdatePasswordPage: React.FC = () => {
  const [state, setState] = useState<'checking' | 'ready' | 'invalid'>(() => (linkErrorInUrl() ? 'invalid' : 'checking'));
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (state !== 'checking') return;
    let done = false;
    const accept = (session: any) => {
      if (done || !session?.user) return;
      done = true;
      setEmail(session.user.email ?? null);
      setState('ready');
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') accept(session);
    });
    supabase.auth.getSession().then(({ data }) => accept(data.session));
    const timeout = window.setTimeout(() => { if (!done) setState('invalid'); }, SESSION_WAIT_MS);
    return () => { subscription.unsubscribe(); window.clearTimeout(timeout); };
  }, [state]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const er: typeof errors = {};
    if (password.length < MIN_PASSWORD_LENGTH) er.password = `La contraseña necesita al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
    if (confirm !== password) er.confirm = 'Las dos contraseñas no coinciden.';
    setErrors(er);
    if (er.password || er.confirm) {
      focusFirstError([[er.password, 'a6-pass'], [er.confirm, 'a6-confirm']]);
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // Entra en la aplicación; las rutas reparten por rol (Pricing, Industrial…).
      window.location.href = '/';
    } catch (err: any) {
      const message = String(err?.message || '').toLowerCase();
      if (message.includes('fetch') || message.includes('network')) setFormError(OFFLINE_MESSAGE);
      else if (message.includes('same') || message.includes('different')) setFormError('La contraseña nueva tiene que ser distinta de la anterior.');
      else if (message.includes('session') || message.includes('expired') || message.includes('jwt')) setState('invalid');
      else setFormError('No hemos podido guardar la contraseña. Inténtalo de nuevo.');
      setSaving(false);
    }
  };

  const back = <Box component="a" href="/login" sx={{ color: J.skyText, fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>Volver a iniciar sesión</Box>;

  return (
    <SimpleLayout narrow>
      <Eyebrow tone="sky">Acceso</Eyebrow>
      {state === 'checking' && <StatusBlock variant="loading" minHeight={320} title="Comprobando el enlace" text="Un momento." />}
      {state === 'ready' && (
        <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <JourneyText variant="title">Elige tu nueva contraseña</JourneyText>
            {email && <JourneyText variant="lead">Para <b style={{ color: J.text }}>{email}</b>.</JourneyText>}
          </Box>
          <Box component="form" noValidate onSubmit={save} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* PRC-030: el gestor de contraseñas asocia la nueva a este email. */}
            {email && <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />}
            <PasswordField
              id="a6-pass" label="Nueva contraseña" value={password} autoComplete="new-password" autoFocus showRequirements
              onChange={(e) => { setPassword(e.target.value); setErrors((x) => ({ ...x, password: undefined })); }}
              onBlur={() => { if (password && password.length < MIN_PASSWORD_LENGTH) setErrors((x) => ({ ...x, password: `La contraseña necesita al menos ${MIN_PASSWORD_LENGTH} caracteres.` })); }}
              error={errors.password}
            />
            <PasswordField
              id="a6-confirm" label="Repite la contraseña" value={confirm} autoComplete="new-password"
              onChange={(e) => { setConfirm(e.target.value); setErrors((x) => ({ ...x, confirm: undefined })); }}
              onBlur={() => { if (confirm && confirm !== password) setErrors((x) => ({ ...x, confirm: 'Las dos contraseñas no coinciden.' })); }}
              error={errors.confirm}
            />
            {formError && <Notice tone="danger">{formError}</Notice>}
            <JourneyButton type="submit" block disabled={saving}>{saving ? 'Guardando…' : 'Guardar y entrar'}</JourneyButton>
          </Box>
          <JourneyText variant="small">Al guardar, entras directamente en la aplicación.</JourneyText>
        </JourneyCard>
      )}
      {state === 'invalid' && (
        <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <StateIcon tone="warn"><LinkOffIcon /></StateIcon>
          <JourneyText variant="title">Este enlace ya no sirve</JourneyText>
          <JourneyText variant="lead">Los enlaces para cambiar la contraseña caducan y solo se pueden usar una vez. Pide uno nuevo y ábrelo en cuanto te llegue.</JourneyText>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <JourneyButton onClick={() => { window.location.href = '/recuperar-contrasena'; }}>Pedir un enlace nuevo</JourneyButton>
            {back}
          </Box>
        </JourneyCard>
      )}
    </SimpleLayout>
  );
};

export default UpdatePasswordPage;
